import React from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import { useQueryClient } from '@tanstack/react-query';
import useInitNikeShop from '@nike/shop-apis/query-hooks/use-init-nike-shop';
import useI18nString from '@nike/shop-apis/assets/i18n/dotcom-cart/use-query';
import ErrorBoundary from '@/components/error-boundary/ErrorBoundary';

import {
  CART_RELATED_ERRORS_TO_EXCLUDE,
  CART_RELATED_WARNINGS_TO_INCLUDE,
} from '@/state/constants';
import useInitPixlee from '@/api/query-hooks/use-init-pixlee';
/**
 * @name ClientSideAppWrapper
 * @description Shared wrapper for /cart and /favorites pages that needs access to page-specific
 * props from getStaticProps, which is why it can't be in _app.js
 */
const ClientSideAppWrapper = ({ headTitleKey, siteKey, children }) => {
  const queryClient = useQueryClient();

  useInitNikeShop({
    shouldUseDynamicImport: process.env.USE_SHOP_COMPONENTS === 'true',
    initNikeShopParams: {
      errorsToExclude: CART_RELATED_ERRORS_TO_EXCLUDE,
      warningsToIncludeInDialog: CART_RELATED_WARNINGS_TO_INCLUDE,
      queryClient,
    },
  });
  useInitPixlee({ siteKey });

  const title = useI18nString({
    stringKey: `head.title.${headTitleKey}`,
    primaryValue: 'Cart. Nike Store.', // default to Cart, should only be used as fallback should translations break somehow
  });
  return (
    <ErrorBoundary>
      <div className="app-shell">
        <Head>
          <title>{title}</title>
          <meta name="viewport" content="initial-scale=1.0, width=device-width" />
        </Head>
        {children}
      </div>
    </ErrorBoundary>
  );
};

ClientSideAppWrapper.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.element]).isRequired,
  headTitleKey: PropTypes.string.isRequired,
  siteKey: PropTypes.string.isRequired,
};

export default ClientSideAppWrapper;
